/**
 * Tenants repository — the ONLY place that touches `tenants` data.
 *
 * Hooks (`useTenants`, `useTenantsStats`, …) and components call these functions;
 * nothing else may call `supabase.from("tenants")` directly. Strangler-Fig
 * per-slice migration (manifest-6):
 *   Step 1 (done) — consolidated scattered `supabase.from("tenants")` here.
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/tenants/*`).
 *   Step 3 (HERE) — staff reads/writes flipped to the Nest client; the
 *                   tenant-portal self-service reads (which use OTHER code
 *                   paths, not this repo) stay on Supabase/RLS.
 *
 * ── Flip scope (owner-vs-staff authz split) ──────────────────────────────────
 *   FLIPPED to Nest (with a Supabase fallback): `list`, `stats`, `statsEnhanced`,
 *     `getById`, `create`, `update`, `softDelete`, `deactivate` — all reached
 *     only by the staff dashboard (Tenants page, AddTenant/EditTenant dialogs,
 *     TenantDetails), whose callers hold `tenants.{view,create,edit,delete}`.
 *   KEPT on Supabase (documented):
 *     - `count` — the dashboard-stats aggregate (`useDashboardStats`) batches it
 *        with many other domain counts; there is no `tenants` count endpoint, so
 *        it stays on Supabase to avoid an N+1 of guarded calls.
 *     - `findByIdNumber` — an EJAR-import sub-step (`save-ejar-contract.ts`) that
 *        runs inside a client-orchestrated import flow alongside other-slice
 *        writes; kept on Supabase until the EJAR import flow migrates.
 *     - The tenant *portal* self-service reads (src/pages/portal/tenant/*,
 *        useTenantData) and cross-domain `tenants` reads (useFinance,
 *        usePropertyAggregates, useTaskPropertyContext, useUnifiedInvite,
 *        TenantsReport, invoices repo) use DIFFERENT code paths — also Supabase,
 *        and tracked as the slice's deferred backlog.
 *
 * Each flipped method tries Nest first (only when an API is configured) and
 * falls back to the original Supabase query on any error — so this is a
 * reversible unit and local dev without the Nest API keeps working. RLS is
 * untouched. List/detail Nest responses are parsed through the shared
 * `tenantSchema` (a too-strict DTO would silently fall back to Supabase — the
 * #1699 lesson).
 *
 * ── Soft-delete convention ───────────────────────────────────────────────────
 * The `tenants` table carries BOTH `deleted_at` and `is_active`:
 *   • `deleted_at` — the soft-delete column. `softDelete()` sets it directly.
 *   • `is_active` — a separate "deactivate" flag (does NOT imply deletion).
 *     `deactivate()` sets this to `false`; the row remains visible.
 * There is no hard `.delete()` here and this repository does not introduce one.
 *
 * Reads via `tenants_with_status` view — the RLS restrictive policy hides
 * `deleted_at IS NOT NULL` rows for normal users automatically on the Supabase
 * path; the Nest path filters `deleted_at IS NULL` explicitly (service key
 * bypasses RLS).
 */
import { API_ROUTES } from '@/api/routes';
import { supabase } from '@/integrations/supabase/client';
import type { TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import {
  reportNestFallback,
  reportNestFallbackOrRethrowAuth,
  reportNestFallbackOrRethrowUnknownCommit,
} from '@/lib/nestFallback';
import { API_URL } from '@/services/api';
import { makeNestRequest } from '@/services/nestRequest';
import { nafathVerificationsRepository } from '@/repositories/nafathVerifications';
import {
  tenantSchema,
  tenantStatsEnhancedRowSchema,
  tenantStatsSchema,
} from '@dallali/shared';
import { z } from 'zod';

const TABLE = 'tenants' as const;
const VIEW = 'tenants_with_status' as const;

/** Columns needed for the canonical tenant list (view adds `status`). */
const LIST_SELECT = '*';

/** Columns for the stats query (active/inactive breakdown). */
const STATS_SELECT = 'id, is_active';

/** Columns for the enhanced-stats query (status from the view). */
const ENHANCED_STATS_SELECT = 'id, status';

// Authenticated Nest request bound to the "tenants" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
// UNSCOPED: no x-active-role forwarded/required. Used by writes (create,
// update, softDelete, deactivate) — WRITE endpoints do NOT carry RowScopeGuard
// (capability-gated only, see tenants.controller.ts), so they must never be
// blocked or rerouted to the Supabase fallback by a missing/unresolved active
// role (issue #3278 — a client-side guard on a shared instance previously did
// exactly that).
const nestRequest = makeNestRequest('tenants');

// Row-scoped variant: list/stats/statsEnhanced/getById ARE RowScopeGuard-
// protected and 403 without x-active-role — mirror the Supabase client's
// per-call header, and short-circuit the boot-race window client-side
// (`requireActiveRole`).
const nestRequestScoped = makeNestRequest('tenants', {
  forwardActiveRole: true,
  requireActiveRole: true,
});

// ── Supabase implementations (fallback for the flipped staff methods, and the
//    live path for the methods kept on Supabase) ───────────────────────────────

async function listViaSupabase(companyId: string) {
  // `tenants_with_status` is a view not in the generated Supabase types.
  // Cast ONLY the relation name so the rest of the chain stays type-checked.
  const { data, error } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from(VIEW as any)
    .select(LIST_SELECT)
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  // `from(VIEW as any)` degrades the inferred row type; assert the real shape.
  return (data ?? []) as unknown as Array<Record<string, unknown>>;
}

async function statsViaSupabase(companyId: string) {
  const { data, error } = await supabase
    .from(TABLE)
    .select(STATS_SELECT)
    .eq('company_id', companyId);
  if (error) throw error;
  const total = data?.length ?? 0;
  const active = data?.filter((t) => t.is_active).length ?? 0;
  return { total, active, inactive: total - active };
}

async function statsEnhancedViaSupabase(companyId: string) {
  // Same `tenants_with_status` view — cast only the relation name (see list()).
  const { data, error } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from(VIEW as any)
    .select(ENHANCED_STATS_SELECT)
    .eq('company_id', companyId);
  if (error) throw error;
  return (data ?? []) as unknown as Array<{ id: string; status?: string }>;
}

async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from(TABLE)
    .select(LIST_SELECT)
    .eq('id', id)
    .eq('company_id', companyId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function createViaSupabase(payload: TablesInsert<'tenants'>) {
  const { data, error } = await supabase
    .from(TABLE)
    .insert(payload)
    .select()
    .single();
  if (error) throw error;
  return data;
}

async function updateViaSupabase(
  id: string,
  patch: TablesUpdate<'tenants'>,
  companyId: string
) {
  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq('id', id)
    .eq('company_id', companyId)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Projection returned by `findByPhone` — just the display-name columns the
 * phone-lookup consumer (`useContactNameByPhone`) needs. Keep this narrow:
 * callers needing more columns should add a new projection, not widen this one.
 */
export interface TenantPhoneContact {
  first_name: string | null;
  last_name: string | null;
  commercial_name: string | null;
}

async function findByPhoneViaSupabase(
  companyId: string,
  candidates: string[],
  limit: number
): Promise<TenantPhoneContact[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('first_name, last_name, commercial_name')
    .eq('company_id', companyId)
    .in('phone', candidates)
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as TenantPhoneContact[];
}

/**
 * Like `findByPhone` but resolves MANY phones in one round-trip and includes
 * `phone` so the caller can key each result back to its conversation — the
 * Conversations inbox resolves a whole page of phones → names at once.
 */
export interface TenantPhoneContactRow extends TenantPhoneContact {
  phone: string | null;
  first_name_en: string | null;
  last_name_en: string | null;
  commercial_name_en: string | null;
}
async function findManyByPhonesViaSupabase(
  companyId: string,
  candidates: string[],
  limit: number
): Promise<TenantPhoneContactRow[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select(
      'phone, first_name, first_name_en, last_name, last_name_en, commercial_name, commercial_name_en'
    )
    .eq('company_id', companyId)
    .in('phone', candidates)
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as TenantPhoneContactRow[];
}

/**
 * Reuse-decision projection: the columns `findReusableByPhone` needs to decide
 * whether an existing same-company tenant is a real record or an
 * invite-placeholder ("orphan") that should be reactivated rather than
 * duplicated. Keep narrow.
 */
export interface ReusableTenant {
  id: string;
  user_id: string | null;
  invitation_status: string | null;
  id_number: string | null;
  is_active: boolean | null;
}

/** Narrow tenant contact row for the task property-context sidebar (fe-tail T9). */
export interface TaskContextTenantRow {
  id: string;
  first_name: string;
  last_name: string;
  first_name_en: string | null;
  last_name_en: string | null;
  phone: string;
  email: string | null;
}

async function findReusableByPhoneViaSupabase(
  companyId: string,
  candidates: string[]
): Promise<ReusableTenant | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, user_id, invitation_status, id_number, is_active')
    .eq('company_id', companyId)
    .in('phone', candidates)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  return ((data ?? [])[0] as ReusableTenant | undefined) ?? null;
}

type TenantListResult = Awaited<ReturnType<typeof listViaSupabase>>;
type TenantStatsEnhancedResult = Awaited<
  ReturnType<typeof statsEnhancedViaSupabase>
>;
type TenantDetailResult = Awaited<ReturnType<typeof getByIdViaSupabase>>;
type TenantCreateResult = Awaited<ReturnType<typeof createViaSupabase>>;
type TenantUpdateResult = Awaited<ReturnType<typeof updateViaSupabase>>;

export const tenantsRepository = {
  /**
   * Company-scoped tenant list via `tenants_with_status` view (newest first).
   * The view adds a derived `status` field (`not_invited` / `not_verified` /
   * `verified`). Staff op — flipped to Nest (company scope resolved server-side
   * from the JWT), Supabase fallback. RLS hides soft-deleted rows on the
   * Supabase path; the Nest path filters `deleted_at IS NULL` explicitly.
   */
  async list(companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequestScoped<unknown[]>(API_ROUTES.tenants.list());
        return z.array(tenantSchema).parse(rows) as unknown as TenantListResult;
      } catch (err) {
        // tenants_with_status is security_invoker=true (authenticated-only SELECT).
        // Re-throw auth errors so the Supabase path isn't called with no session
        // (would 42501). QueryCache.onError's isNestAuthError gate suppresses it.
        reportNestFallbackOrRethrowAuth('tenants.list', err);
      }
    }
    return listViaSupabase(companyId);
  },

  /**
   * Company-scoped tenant list with attached nafath_verifications data.
   * Calls `list()` first, then batch-fetches COMPLETED nafath rows for verified
   * tenants (id_type='national'). Returns rows with a `nafath` field attached.
   * The nafath_verifications table IS in auto-generated types — no cast needed.
   */
  async listWithNafath(companyId: string) {
    const rows = await this.list(companyId);
    const typedRows = rows as Array<Record<string, unknown>>;

    const nafathMap = new Map<string, Record<string, unknown>>();
    const verifiedTenants = typedRows.filter(
      (t) => t.status === 'verified' && t.id_type === 'national'
    );
    if (verifiedTenants.length > 0) {
      const verifiedIdNumbers = verifiedTenants
        .map((t) => t.id_number as string)
        .filter(Boolean);
      if (verifiedIdNumbers.length > 0) {
        const nafathRows =
          await nafathVerificationsRepository.listCompletedByNationalIds(
            verifiedIdNumbers,
            companyId
          );
        for (const row of nafathRows) {
          nafathMap.set(
            row.national_id,
            row as unknown as Record<string, unknown>
          );
        }
      }
    }

    return typedRows.map((t) => ({
      ...t,
      nafath: nafathMap.get(t.id_number as string) ?? null,
    }));
  },

  /**
   * Active/inactive count for the company. Reads the `tenants` table directly so
   * we get the real `is_active` column. Staff op — flipped to Nest, Supabase
   * fallback. The Nest response is the aggregated `{ total, active, inactive }`.
   */
  async stats(companyId: string) {
    if (API_URL) {
      try {
        const out = await nestRequestScoped<unknown>(API_ROUTES.tenants.stats());
        return tenantStatsSchema.parse(out);
      } catch (err) {
        reportNestFallback('tenants.stats', err);
      }
    }
    return statsViaSupabase(companyId);
  },

  /**
   * Enhanced stats via the `tenants_with_status` view: total + invited count.
   * The `status` column is derived by the view. Staff op — flipped to Nest,
   * Supabase fallback. Parsed loosely (id + optional status) so a column-set
   * match isn't required to avoid silent fallback.
   */
  async statsEnhanced(companyId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequestScoped<unknown[]>(
          API_ROUTES.tenants.statsEnhanced()
        );
        return tenantStatsEnhancedRowSchema
          .array()
          .parse(rows) as unknown as TenantStatsEnhancedResult;
      } catch (err) {
        // tenants_with_status is security_invoker=true — same guard as list().
        reportNestFallbackOrRethrowAuth('tenants.statsEnhanced', err);
      }
    }
    return statsEnhancedViaSupabase(companyId);
  },

  /**
   * Single tenant by id, company-scoped. Staff op — flipped to Nest, Supabase
   * fallback. The Nest response is the `tenants` row, validated through the
   * shared `tenantSchema`.
   */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequestScoped<unknown>(API_ROUTES.tenants.getById(id));
        return (row
          ? tenantSchema.parse(row)
          : null) as unknown as TenantDetailResult;
      } catch (err) {
        reportNestFallback('tenants.getById', err);
      }
    }
    return getByIdViaSupabase(id, companyId);
  },

  /**
   * Insert a tenant (payload must already carry `company_id`); returns the row.
   * Staff op — flipped to Nest (POST), Supabase fallback on a safe 4xx. Nest
   * injects company_id from the JWT and ignores any body company_id (whitelist
   * strips it).
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the Supabase
   * fallback only runs when Nest verifiably did not commit (safe 4xx /
   * unconfigured). An ambiguous outcome (network, timeout, 5xx, parse failure
   * after a 2xx) RETHROWS instead of re-issuing the insert — a fallback after a
   * committed-but-lost Nest write would double-create the tenant.
   */
  async create(payload: TablesInsert<'tenants'>) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.tenants.create(), {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        return tenantSchema.parse(row) as unknown as TenantCreateResult;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit('tenants.create', err);
      }
    }
    return createViaSupabase(payload);
  },

  /**
   * Update a company-scoped tenant; returns the updated row (or null if not
   * found or access denied). Staff op — flipped to Nest (PUT), Supabase fallback.
   */
  async update(id: string, patch: TablesUpdate<'tenants'>, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.tenants.update(id), {
          method: 'PUT',
          body: JSON.stringify(patch),
        });
        return (row
          ? tenantSchema.parse(row)
          : null) as unknown as TenantUpdateResult;
      } catch (err) {
        reportNestFallback('tenants.update', err);
      }
    }
    return updateViaSupabase(id, patch, companyId);
  },

  /**
   * Soft-delete a company-scoped tenant (`deleted_at = now()`). Staff op —
   * flipped to Nest (DELETE → soft delete server-side), Supabase fallback. NEVER
   * a hard `.delete()`.
   */
  async softDelete(id: string, companyId: string) {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.tenants.remove(id), {
          method: 'DELETE',
        });
        return;
      } catch (err) {
        reportNestFallback('tenants.softDelete', err);
      }
    }
    const { error } = await supabase
      .from(TABLE)
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id)
      .eq('company_id', companyId);
    if (error) throw error;
  },

  /**
   * Deactivate a company-scoped tenant (`is_active = false`). A SEPARATE
   * operation from soft-delete: the row remains fully visible. Staff op —
   * flipped to Nest (PATCH), Supabase fallback.
   */
  async deactivate(id: string, companyId: string) {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.tenants.deactivate(id), {
          method: 'PATCH',
        });
        return;
      } catch (err) {
        reportNestFallback('tenants.deactivate', err);
      }
    }
    const { error } = await supabase
      .from(TABLE)
      .update({ is_active: false })
      .eq('id', id)
      .eq('company_id', companyId);
    if (error) throw error;
  },

  /**
   * Company-scoped tenant count (dashboard stats). KEPT on Supabase — batched
   * with many other domain counts in `useDashboardStats`; no Nest endpoint.
   * RLS hides soft-deleted rows automatically.
   */
  async count(companyId: string) {
    const { count, error } = await supabase
      .from(TABLE)
      .select('id', { count: 'exact', head: true })
      .eq('company_id', companyId);
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * Look up display-name columns for any tenant whose `phone` matches one of
   * `candidates`, scoped to the company. Used by `useContactNameByPhone` to
   * resolve a name shown beside the phone in the messages list — historical
   * rows exist in several phone shapes (canonical `966XXXXXXXXX`, `+966X`,
   * local `05X`, bare 9-digit), so the caller generates every plausible
   * variant and we pass them all through PostgREST's `IN(...)`.
   *
   * KEPT on Supabase — there is no Nest endpoint for this projection. Returns
   * a narrow `{ first_name, last_name, commercial_name }[]` — do NOT widen
   * this projection without a corresponding caller need.
   */
  async findByPhone(
    companyId: string,
    candidates: string[],
    limit = 50
  ): Promise<TenantPhoneContact[]> {
    return findByPhoneViaSupabase(companyId, candidates, limit);
  },
  async findManyByPhones(
    companyId: string,
    candidates: string[],
    limit = 200
  ): Promise<TenantPhoneContactRow[]> {
    return findManyByPhonesViaSupabase(companyId, candidates, limit);
  },

  /**
   * Find a reusable same-company tenant by phone (any historical shape — pass
   * `buildPhoneCandidates(canonical)`), excluding soft-deleted rows. Used by the
   * create/invite flows to DETECT & REUSE an existing tenant instead of hitting
   * the global-unique index with a duplicate (the "data is used" trap). Returns
   * the newest match or null. Company-scoped; KEPT on Supabase (no Nest
   * endpoint — a pre-write lookup internal to the client-orchestrated flow).
   *
   * Cross-company duplicates are handled separately and earlier by
   * `assertPhoneAvailableAcrossCompanies`, so any hit here is same-company and
   * safe to reuse.
   */
  async findReusableByPhone(
    companyId: string,
    candidates: string[]
  ): Promise<ReusableTenant | null> {
    if (candidates.length === 0) return null;
    return findReusableByPhoneViaSupabase(companyId, candidates);
  },

  /**
   * Look up a tenant by national/CR id_number within a company (EJAR import:
   * decide insert-vs-update for an imported contract's tenant). Company-scoped.
   * KEPT on Supabase — a sub-step of the client-orchestrated EJAR import flow.
   */
  async findByIdNumber(idNumber: string, companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(
        'id, tenant_type, commercial_name, cr_number, unified_number, user_id'
      )
      .eq('company_id', companyId)
      .eq('id_number', idNumber)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  /**
   * Contact/identity card for the receipt detail (party block on the receipt
   * UI/PDF), company-scoped. KEPT on Supabase — enrichment sub-read of
   * `useReceipt`. NEVER throws: the pre-consolidation site destructured
   * `{ data }` only, so a failed read renders the receipt without the party
   * card instead of erroring the whole receipt query.
   */
  async getReceiptContact(id: string, companyId: string) {
    const { data } = await supabase
      .from(TABLE)
      .select(
        'first_name, last_name, first_name_en, last_name_en, commercial_name, commercial_name_en, tenant_type, phone, email, address, cr_number, id_number, id_type'
      )
      .eq('id', id)
      .eq('company_id', companyId)
      .maybeSingle();
    return data;
  },

  /**
   * Tenants (with their contract/unit context) for a set of ids — step 2 of
   * the property-drawer Tenants tab (the caller filters the joined contracts
   * to the property in-memory). Company-scoped. KEPT on Supabase.
   */
  async listWithContractUnitsByIds(companyId: string, tenantIds: string[]) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(
        `
          *,
          contracts:contracts!contracts_tenant_id_fkey(
            id,
            status,
            property_id,
            unit:units(unit_number)
          )
        `
      )
      .eq('company_id', companyId)
      .in('id', tenantIds);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Contact/identity columns for the tenants report. Company-scoped. KEPT on
   * Supabase.
   */
  async listReportRows(companyId: string) {
    const { data, error } = await supabase
      .from(TABLE)
      .select(
        'id, first_name, last_name, phone, nationality, id_number, is_active'
      )
      .eq('company_id', companyId);
    if (error) throw error;
    return data ?? [];
  },

  /**
   * One tenant from the `tenants_with_status` VIEW (derived
   * verified/not_verified/not_invited status), scoped by id AND company_id.
   * The view is in the generated types under Views (not Tables), so the typed
   * client's .from() rejects it — the relation-name cast is moved verbatim
   * from the pre-consolidation site. Throws on error; null when absent.
   */
  async getWithStatusById(id: string, companyId: string) {
    const VIEW = 'tenants_with_status' as const;
    const { data, error } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from(VIEW as any)
      .select('*')
      .eq('id', id)
      .eq('company_id', companyId)
      .maybeSingle();
    if (error) throw error;
    return data as unknown;
  },

  /**
   * The narrow tenant contact row for the task property-context sidebar
   * (fe-tail T9). NEVER throws — the inline original destructured `{ data }`
   * only, so an error renders the card without the tenant section.
   */
  async taskContextTenant(
    tenantId: string,
    companyId: string,
  ): Promise<TaskContextTenantRow | null> {
    const { data } = await supabase
      .from(TABLE)
      .select(
        'id, first_name, last_name, first_name_en, last_name_en, phone, email',
      )
      .eq('id', tenantId)
      .eq('company_id', companyId)
      .maybeSingle();
    return (data as TaskContextTenantRow | null) ?? null;
  },
};
